import { useNavigate, useSearch } from "@tanstack/react-router";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { AccountDetailContent } from "@/features/renewals/components/AccountDetailContent";
import { AccountQuickInfo } from "@/features/renewals/components/AccountQuickInfo";

import type { CustomerTiersSearch } from "..";

/**
 * Account drawer for the ARR leaderboard. Mirrors the renewals CustomerDrawer,
 * but bound to /customer-tiers so it opens *in place* over the leaderboard.
 * Visibility is derived from the route's search params:
 *
 *   ?account=<qbId>              → quick view
 *   ?account=<qbId>&expanded=1   → expanded (full account) view
 *   no `account`                 → closed
 *
 * Reuses the renewals account views — they're keyed only by `qbId`, which is
 * the leaderboard's `counterparty_id`. The `tab` param is preserved across
 * open/expand/close so dismissing the drawer returns you to the leaderboard
 * exactly where you were.
 */
export function AccountDrawer() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/customer-tiers" }) as CustomerTiersSearch;
  const qbId = search.account ?? null;
  const expanded = !!search.expanded;

  // Merge into the existing search so `tab` (and anything else) survives.
  // Setting a key to `undefined` drops it from the URL.
  const go = (next: Partial<CustomerTiersSearch>) =>
    navigate({
      to: "/customer-tiers",
      search: (prev: CustomerTiersSearch) => ({ ...prev, ...next }),
    });

  return (
    <EntityDrawer
      open={qbId != null}
      onOpenChange={(v) => {
        // ESC / backdrop closes in quick mode; expanded mode is the page and
        // ignores these. Keep the tab, drop the account.
        if (!v && !expanded) go({ account: undefined, expanded: undefined });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => {
        if (qbId != null) go({ expanded: exp ? true : undefined });
      }}
      onClose={() => {
        if (qbId == null) return;
        // Peel one layer at a time: expanded → quick, then quick → closed.
        if (expanded) go({ expanded: undefined });
        else go({ account: undefined, expanded: undefined });
      }}
      title="Account"
      subtitle={qbId != null ? `QB-${qbId}` : ""}
    >
      {qbId != null &&
        (expanded ? <AccountDetailContent qbId={qbId} /> : <AccountQuickInfo qbId={qbId} />)}
    </EntityDrawer>
  );
}
